import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, TextField, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Alert, Snackbar, Checkbox, FormControlLabel, FormGroup
} from '@mui/material';
import { ArrowBack, Refresh, Campaign, Send } from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

export default function Announcements() {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', target_roles: [], channels: ['in_app'] });
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchAnnouncements = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/announcements');
      setAnnouncements(data.announcements || []);
    } catch { setAnnouncements([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const handleSend = async () => {
    if (!form.title || !form.body || form.channels.length === 0) return;
    setSending(true);
    try {
      await apiRequest('/admin/announcements', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setSnackbar({ open: true, message: 'Comunicado enviado', severity: 'success' });
      setDialogOpen(false);
      setForm({ title: '', body: '', target_roles: [], channels: ['in_app'] });
      fetchAnnouncements();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Error', severity: 'error' });
    }
    setSending(false);
  };

  const toggleChannel = (ch) => {
    setForm(f => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch]
    }));
  };

  const toggleRole = (role) => {
    setForm(f => ({
      ...f,
      target_roles: f.target_roles.includes(role) ? f.target_roles.filter(r => r !== role) : [...f.target_roles, role]
    }));
  };

  const formatTime = (t) => new Date(t).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/control-room')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vincula</Typography>
              <Chip label="Comunicados" size="small" variant="outlined" icon={<Campaign sx={{ fontSize: 14 }} />} />
            </Stack>
            <Button variant="contained" startIcon={<Send />} onClick={() => setDialogOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
              Nuevo comunicado
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : announcements.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No hay comunicados</Typography></Box>
          ) : (
            announcements.map((a, idx) => (
              <React.Fragment key={a.id}>
                {idx > 0 && <Divider />}
                <Box sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="body1" fontWeight={700}>{a.title}</Typography>
                    {a.channels && (
                      <Stack direction="row" spacing={0.5}>
                        {a.channels.replace(/[{}]/g, '').split(',').map(ch => (
                          <Chip key={ch} label={ch} size="small" sx={{ height: 18, fontSize: 9, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{a.body}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.creator_name} · {formatTime(a.created_at)}
                    {a.target_roles && a.target_roles !== '{}' && ` · Para: ${a.target_roles.replace(/[{}]/g, '')}`}
                  </Typography>
                </Box>
              </React.Fragment>
            ))
          )}
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Nuevo comunicado</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField fullWidth label="Mensaje" multiline rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <Typography variant="body2" fontWeight={600}>Canales de envío:</Typography>
            <FormGroup row>
              {[{ key: 'in_app', label: 'In-App' }, { key: 'email', label: 'Email' }, { key: 'push', label: 'Push' }].map(ch => (
                <FormControlLabel key={ch.key} control={<Checkbox checked={form.channels.includes(ch.key)} onChange={() => toggleChannel(ch.key)} size="small" />} label={ch.label} />
              ))}
            </FormGroup>
            <Typography variant="body2" fontWeight={600}>Destinatarios (vacío = todos):</Typography>
            <FormGroup row>
              {[{ key: 'patient', label: 'Pacientes' }, { key: 'family', label: 'Familiares' }, { key: 'employee', label: 'Acompañantes' }].map(r => (
                <FormControlLabel key={r.key} control={<Checkbox checked={form.target_roles.includes(r.key)} onChange={() => toggleRole(r.key)} size="small" />} label={r.label} />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSend} disabled={sending || !form.title || !form.body}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
            sx={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
